/**
 * InvitationsPage Component
 *
 * Lists pending reflection sharing invitations for the current user.
 * Users can accept (creates a copy) or decline each invitation.
 */

import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail } from "lucide-react";
import { Layout } from "@/components/Layout";
import { InvitationCard } from "@/components/InvitationCard";
import {
  getPendingInvitations,
  acceptInvitation,
  declineInvitation,
} from "@/services/invitations";

export function InvitationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: invitations,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["invitations", "pending"],
    queryFn: getPendingInvitations,
  });

  const handleAccept = async (invitationId: string) => {
    const result = await acceptInvitation(invitationId);
    queryClient.invalidateQueries({ queryKey: ["invitations"] });
    queryClient.invalidateQueries({ queryKey: ["reflections"] });
    navigate(`/reflections/${result.reflection_id}`);
  };

  const handleDecline = async (invitationId: string) => {
    await declineInvitation(invitationId);
    queryClient.invalidateQueries({ queryKey: ["invitations"] });
  };

  return (
    <Layout>
      <div className="container mx-auto max-w-2xl px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-1">
            Shared with you
          </h1>
          <p className="text-muted-foreground">
            Reflections that others have shared with you
          </p>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading...</span>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
            <p className="text-destructive">
              Error loading invitations: {(error as Error).message}
            </p>
          </div>
        )}

        {!isLoading && !error && invitations?.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
            <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No pending shares</p>
          </div>
        )}

        {!isLoading && invitations && invitations.length > 0 && (
          <div className="space-y-4">
            {invitations.map((invitation) => (
              <InvitationCard
                key={invitation.id}
                invitation={invitation}
                onAccept={handleAccept}
                onDecline={handleDecline}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
